// MVP API 의 대화 중계 (api/internal/httpapi/llm.go). 배포는 Vercel rewrite, 로컬은 Vite proxy 가 /api 를 API 서버로 넘긴다.
// 응답 스트림을 읽어 이벤트로 돌려준다. 이용권·모델 공급자 자격은 화면이 모른다 — 보내는 것은 로그인 토큰과 메시지뿐이다
import { z } from 'zod';

import { ChatRequestError, toChatError, type ChatMessage, type StreamEvent } from '../domain/chat';
import { createSseParser, type RawEvent } from '../domain/sse';

const Delta = z.object({ text: z.string() });
const Failure = z.object({ code: z.string() });

/** 중계가 보낸 이벤트를 경계에서 파싱한다. 모르는 이름·깨진 본문은 null — 조용히 건너뛴다 */
function parseEvent(raw: RawEvent): StreamEvent | null {
  let body: unknown;
  try {
    body = raw.data === '' ? {} : JSON.parse(raw.data);
  } catch {
    // 본문을 못 읽는 error 도 실패다 — 성공으로 읽히면 안 된다
    return raw.name === 'error' ? { type: 'error', code: 'llm_provider_error' } : null;
  }
  switch (raw.name) {
    case 'delta': {
      const delta = Delta.safeParse(body);
      return delta.success ? { type: 'delta', text: delta.data.text } : null;
    }
    case 'done':
      return { type: 'done' };
    case 'error': {
      // 코드를 못 읽는 error 도 실패다 — 성공으로 읽히면 안 된다
      const failure = Failure.safeParse(body);
      return { type: 'error', code: failure.success ? failure.data.code : 'llm_provider_error' };
    }
    default:
      return null;
  }
}

/**
 * 대화를 시작하고 이벤트를 도착 순서대로 내보낸다.
 * - 스트림이 열리기 전의 실패(401·402·4xx·5xx·네트워크)는 ChatRequestError 로 던진다
 * - 도중에 연결이 끊기면 던지지 않고 끝난다 — done·error 없이 끝났다는 사실이 호출자에게 「끊김」이다
 * - signal 로 중단하면 AbortError 가 그대로 나간다
 */
export async function* streamChat(endpoint: string, token: string, messages: readonly ChatMessage[], signal: AbortSignal): AsyncGenerator<StreamEvent> {
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ messages }),
      signal,
    });
  } catch (error) {
    if (signal.aborted) throw error;
    throw new ChatRequestError('unavailable');
  }
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    throw new ChatRequestError(toChatError(res.status, (body as { error?: unknown } | null)?.error));
  }
  if (!res.body) throw new ChatRequestError('failed');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      for (const raw of parser.feed(decoder.decode(value, { stream: true }))) {
        const event = parseEvent(raw);
        if (event) yield event;
      }
    }
  } catch (error) {
    if (signal.aborted) throw error;
    // 읽다가 연결이 끊겼다 — 끝났다고만 알린다
  } finally {
    void reader.cancel().catch(() => undefined);
  }
}
